import { Card, CardColor, isDrawCard, isWildCard } from './cards.js';

/** Host-tunable house rules. Every field has a sane default in `DEFAULT_RULES`. */
export interface RoomRules {
  /** Cards dealt to each player at the start of the round. */
  startingHand: number;
  /** Seconds a player has to act before the AFK bot acts for them. */
  turnSeconds: number;
  /** Allow answering a +2 with another +2 (and +4 with +4) instead of drawing. */
  stackDrawTwo: boolean;
  stackDrawFour: boolean;
  /** Wild Draw Four may only be played when you hold no card of the active colour. */
  enforceWildFourLegality: boolean;
  /** The next player may challenge a Wild Draw Four (`AwaitingChallenge` phase). */
  challengeWildFour: boolean;
  /** Cards drawn by a player caught without calling UNO. */
  unoPenalty: number;
  /** A drawn card may be played immediately if it is legal. */
  playDrawnCard: boolean;
  /** Keep drawing until a playable card appears (instead of drawing exactly one). */
  drawUntilPlayable: boolean;
  /** Playing a card is optional after drawing — otherwise the turn passes immediately. */
  jumpIn: boolean;
}

export const DEFAULT_RULES: RoomRules = {
  startingHand: 7,
  turnSeconds: 30,
  stackDrawTwo: true,
  stackDrawFour: true,
  enforceWildFourLegality: true,
  challengeWildFour: true,
  unoPenalty: 2,
  playDrawnCard: true,
  drawUntilPlayable: false,
  jumpIn: false,
};

export const RULE_LIMITS = {
  startingHand: { min: 1, max: 10 },
  turnSeconds: { min: 10, max: 180 },
  unoPenalty: { min: 1, max: 4 },
} as const;

export function sanitizeRules(input: Partial<RoomRules> | undefined): RoomRules {
  const r = { ...DEFAULT_RULES, ...(input ?? {}) };
  const clamp = (v: number, lo: number, hi: number, fallback: number) =>
    Number.isFinite(v) ? Math.min(hi, Math.max(lo, Math.round(v))) : fallback;

  return {
    startingHand: clamp(r.startingHand, RULE_LIMITS.startingHand.min, RULE_LIMITS.startingHand.max, DEFAULT_RULES.startingHand),
    turnSeconds: clamp(r.turnSeconds, RULE_LIMITS.turnSeconds.min, RULE_LIMITS.turnSeconds.max, DEFAULT_RULES.turnSeconds),
    stackDrawTwo: !!r.stackDrawTwo,
    stackDrawFour: !!r.stackDrawFour,
    enforceWildFourLegality: !!r.enforceWildFourLegality,
    challengeWildFour: !!r.challengeWildFour,
    unoPenalty: clamp(r.unoPenalty, RULE_LIMITS.unoPenalty.min, RULE_LIMITS.unoPenalty.max, DEFAULT_RULES.unoPenalty),
    playDrawnCard: !!r.playDrawnCard,
    drawUntilPlayable: !!r.drawUntilPlayable,
    jumpIn: !!r.jumpIn,
  };
}

/** The parts of the table state that decide whether a card is legal right now. */
export interface MatchContext {
  /** Colour currently in force (the chosen colour when the top card is a wild). */
  activeColor: CardColor;
  /** Top card of the discard pile. */
  topCard: Card;
  /** Accumulated unresolved draw penalty (0 when nothing is pending). */
  pendingDraw: number;
  /** Which kind of draw card built up `pendingDraw`. */
  pendingDrawKind: 'draw2' | 'wild4' | null;
  rules: RoomRules;
  /** The acting player's hand — needed for the Wild Draw Four legality rule. */
  hand: Card[];
}

/**
 * Single source of truth for "may this card be played?".
 * The server calls it to authorise actions; the client calls it only to grey out cards.
 */
export function canPlayCard(card: Card, ctx: MatchContext): boolean {
  // While a draw penalty is stacking, the only legal replies are further draw cards
  // of the matching type (when the corresponding house rule is on).
  if (ctx.pendingDraw > 0) {
    if (ctx.pendingDrawKind === 'draw2') {
      if (card.kind === 'draw2' && ctx.rules.stackDrawTwo) return true;
      // A +4 may also be laid on a +2 stack when both stacking rules are enabled.
      if (card.kind === 'wild4' && ctx.rules.stackDrawTwo && ctx.rules.stackDrawFour) {
        return wildFourAllowed(ctx);
      }
      return false;
    }
    if (ctx.pendingDrawKind === 'wild4') {
      if (card.kind === 'wild4' && ctx.rules.stackDrawFour) return wildFourAllowed(ctx);
      return false;
    }
    return false;
  }

  if (card.kind === 'wild') return true;
  if (card.kind === 'wild4') return wildFourAllowed(ctx);

  if (card.color === ctx.activeColor) return true;
  if (card.kind === 'number' && ctx.topCard.kind === 'number') {
    return card.value === ctx.topCard.value;
  }
  return !isWildCard(ctx.topCard) && card.kind === ctx.topCard.kind;
}

/**
 * A Wild Draw Four is only legal when the player holds nothing in the active colour
 * (unless the host disabled the restriction). Wilds in hand do not count.
 */
export function wildFourAllowed(ctx: MatchContext): boolean {
  if (!ctx.rules.enforceWildFourLegality) return true;
  return !ctx.hand.some((c) => !isWildCard(c) && c.color === ctx.activeColor);
}

export function playableCardIds(ctx: MatchContext): string[] {
  return ctx.hand.filter((c) => canPlayCard(c, ctx)).map((c) => c.id);
}

export function hasAnyPlayableCard(ctx: MatchContext): boolean {
  return ctx.hand.some((c) => canPlayCard(c, ctx));
}

export { isDrawCard };
